import { Injectable } from '@angular/core';
import { Dimensions, ImageTransform, ExifTransform } from '../interfaces/image-cropper.interfaces';

@Injectable({
  providedIn: 'root',
})
export class ImageTransformService {
  /**
   * Calculates the transformed size based on rotation and aspect ratio
   */
  private getTransformedSize(
    originalSize: Dimensions,
    canvasRotation: number,
    exifTransform: ExifTransform,
    aspectRatio: number,
    containWithinAspectRatio: boolean
  ): Dimensions {
    const totalRotation: number = canvasRotation + exifTransform.rotate;

    if (containWithinAspectRatio) {
      if (totalRotation % 2) {
        const minWidthToContain: number = originalSize.width * aspectRatio;
        const minHeightToContain: number = originalSize.height / aspectRatio;
        return {
          width: Math.max(originalSize.height, minWidthToContain),
          height: Math.max(originalSize.width, minHeightToContain),
        };
      } else {
        const minWidthToContain: number = originalSize.height * aspectRatio;
        const minHeightToContain: number = originalSize.width / aspectRatio;
        return {
          width: Math.max(originalSize.width, minWidthToContain),
          height: Math.max(originalSize.height, minHeightToContain),
        };
      }
    }

    if (totalRotation % 2) {
      return {
        height: originalSize.width,
        width: originalSize.height,
      };
    }
    return {
      width: originalSize.width,
      height: originalSize.height,
    };
  }

  /**
   * Transforms the image base64 data with rotation and flipping
   */
  public transformImageBase64(
    originalImage: HTMLImageElement,
    originalBase64: string,
    originalSize: Dimensions,
    canvasRotation: number,
    exifTransform: ExifTransform,
    containWithinAspectRatio: boolean,
    aspectRatio: number
  ): string {
    const totalRotation: number = canvasRotation + exifTransform.rotate;

    if (totalRotation === 0 && !exifTransform.flip && !containWithinAspectRatio) {
      return originalBase64;
    }

    const transformedSize: Dimensions = this.getTransformedSize(
      originalSize,
      canvasRotation,
      exifTransform,
      aspectRatio,
      containWithinAspectRatio
    );

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = transformedSize.width;
    canvas.height = transformedSize.height;
    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

    ctx.setTransform(exifTransform.flip ? -1 : 1, 0, 0, 1, canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI * (totalRotation / 2));
    ctx.drawImage(originalImage, -originalSize.width / 2, -originalSize.height / 2);

    return canvas.toDataURL();
  }

  /**
   * Creates CSS transform style from ImageTransform object
   */
  public createCssTransform(transform: ImageTransform): string {
    return (
      'scaleX(' +
      (transform.scale || 1) * (transform.flipH ? -1 : 1) +
      ')' +
      'scaleY(' +
      (transform.scale || 1) * (transform.flipV ? -1 : 1) +
      ')' +
      'rotate(' +
      (transform.rotate || 0) +
      'deg)'
    );
  }
}
